import { useApi } from '@/composables/api.ts'
import { useFolderTreeBuilder } from './use-folder-tree-builder'
import { useFolderTreeReconciler } from './use-folder-tree-reconciler'
import type { useTaskUploads } from './use-task-uploads'

export function useFolderTreeUploader(
  taskUploads: ReturnType<typeof useTaskUploads>,
) {
  const {
    getTaskUploadById,
    updateTaskUploadItemStatus,
    setTaskUploadFolderNode,
  } = taskUploads
  const { deepBoxAPI } = useApi()
  const { buildApiFolderTree, toApiFolders } = useFolderTreeBuilder()
  const { markFolderTreeProcessing, markFolderTreeError, reconcileFolderTree } =
    useFolderTreeReconciler(taskUploads)

  /**
   * Upload the entire folder tree in a single API call, then reconcile all
   * folder statuses from the response.
   */
  async function onUploadFolderTree(taskUploadId: number): Promise<void> {
    const taskUpload = getTaskUploadById(taskUploadId)
    if (!taskUpload?.folders || Object.keys(taskUpload.folders).length === 0)
      return

    const targetNode = taskUpload.target.node
    if (!targetNode?.nodeId) {
      // Mark every folder as errored
      const apiFolders = buildApiFolderTree(taskUpload.folders)
      await markFolderTreeError(
        taskUploadId,
        apiFolders,
        new Error('UPLOAD_PARENT_NODE_ID_CAN_NOT_BE_EMPTY'),
      )
      return
    }

    const apiFolders = buildApiFolderTree(taskUpload.folders)
    if (apiFolders.length === 0) return

    // Mark all as processing
    markFolderTreeProcessing(taskUploadId, apiFolders)

    const abortController = new AbortController()
    for (const metaFolder of apiFolders) {
      setTaskUploadFolderNode(taskUploadId, metaFolder._folderId, {
        abortController,
      })
    }

    try {
      const { data } =
        await deepBoxAPI.value.deepBoxesBoxesFilesPath.createById(
          taskUpload.target.typeId,
          taskUpload.target.boxId,
          targetNode.nodeId,
          toApiFolders(apiFolders),
          abortController,
        )
      await reconcileFolderTree(taskUploadId, apiFolders, data ?? [])
    } catch (error) {
      await markFolderTreeError(taskUploadId, apiFolders, error as Error)
    } finally {
      updateTaskUploadItemStatus(taskUploadId)
    }
  }

  return { onUploadFolderTree }
}
